/**
 * Raid input: one thumb on a phone, WASD on a desktop.
 *
 * The camera never rotates (see `SceneView.followPose`), so the stick vector needs no camera
 * transform — just two fixed signs. The mapping is:
 *
 *     screen-up  = world **+Z**   (the raider walks deeper into the room, away from the door)
 *     screen-right = world **−X**
 *
 * That −X is not a matter of taste. For any camera above the floor plane, `right × up` must point
 * back at the viewer (+Y), so one of the two axes is necessarily negative; the camera's side of the
 * room only decides *which* one. Getting this wrong is invisible in code and obvious in the hand —
 * it shipped once with both axes inverted — so `tools/raid-check.mjs` measures the raider's actual
 * on-screen movement and fails if W does not go up or D does not go right.
 *
 * The joystick is *floating*: it appears wherever the thumb lands on the left half, which is the
 * difference between a control that works on a 6-inch screen and one that doesn't.
 */

import type { RaidInput } from '../sim/types'

const KEY_MAP: Record<string, keyof typeof KEYS> = {
  KeyW: 'up',
  ArrowUp: 'up',
  KeyS: 'down',
  ArrowDown: 'down',
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  Space: 'jump',
}

const KEYS = { up: false, down: false, left: false, right: false, jump: false }

const STICK_RADIUS = 54

export class RaidControls {
  readonly stickEl: HTMLDivElement
  private knobEl: HTMLDivElement
  private keys = { ...KEYS }
  private pointerId: number | null = null
  private origin = { x: 0, y: 0 }
  private vector = { x: 0, y: 0 }
  private jumpQueued = false
  private jumpHeld = false
  private enabled = false

  constructor(host: HTMLElement) {
    this.stickEl = document.createElement('div')
    this.stickEl.className = 'stick'
    this.knobEl = document.createElement('div')
    this.knobEl.className = 'stick-knob'
    this.stickEl.appendChild(this.knobEl)
    this.stickEl.style.display = 'none'
    host.appendChild(this.stickEl)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    // Listen on `window`, not on the UI host: the host is `pointer-events: none`
    // so it is never a hit target and would never see a touch. Window catches
    // taps wherever they land — on the canvas or through the HUD.
    window.addEventListener('pointerdown', this.onPointerDown)
    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerUp)
  }

  /** Taps on real controls must not also drive the raider. */
  private isUiTarget(e: PointerEvent): boolean {
    const target = e.target as HTMLElement | null
    return Boolean(target?.closest?.('button, input, .replay-timeline'))
  }

  setEnabled(on: boolean): void {
    this.enabled = on
    if (!on) this.release()
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return
    const k = KEY_MAP[e.code]
    if (!k) return
    e.preventDefault()
    if (k === 'jump' && !this.keys.jump) this.jumpQueued = true
    this.keys[k] = true
  }

  private onKeyUp = (e: KeyboardEvent): void => {
    const k = KEY_MAP[e.code]
    if (!k) return
    this.keys[k] = false
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.enabled || this.isUiTarget(e)) return
    // Right half is jump; left half is the stick. On desktop a mouse still works.
    if (e.clientX > window.innerWidth * 0.55) {
      this.jumpQueued = true
      this.jumpHeld = true
      return
    }
    if (this.pointerId !== null) return
    this.pointerId = e.pointerId
    this.origin = { x: e.clientX, y: e.clientY }
    this.stickEl.style.display = 'block'
    this.stickEl.style.left = `${e.clientX}px`
    this.stickEl.style.top = `${e.clientY}px`
    this.knobEl.style.transform = 'translate(-50%, -50%)'
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (this.pointerId !== e.pointerId) return
    const dx = e.clientX - this.origin.x
    const dy = e.clientY - this.origin.y
    const len = Math.hypot(dx, dy)
    const clamped = Math.min(len, STICK_RADIUS)
    const nx = len > 0 ? (dx / len) * clamped : 0
    const ny = len > 0 ? (dy / len) * clamped : 0
    this.knobEl.style.transform = `translate(calc(-50% + ${nx}px), calc(-50% + ${ny}px))`
    // Dead zone keeps a resting thumb from creeping the figure into a trap.
    const mag = clamped / STICK_RADIUS
    const live = mag < 0.16 ? 0 : (mag - 0.16) / 0.84
    this.vector = len > 0 ? { x: (dx / len) * live, y: (dy / len) * live } : { x: 0, y: 0 }
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (e.clientX > window.innerWidth * 0.55) this.jumpHeld = false
    if (this.pointerId !== e.pointerId) return
    this.release()
  }

  private release(): void {
    this.pointerId = null
    this.vector = { x: 0, y: 0 }
    this.stickEl.style.display = 'none'
  }

  /**
   * Consume the current intent. Jump is edge-triggered so holding won't hop.
   *
   * The two sign flips are the whole screen->world mapping, and they are not interchangeable with
   * each other. See the header: screen-up is world **+Z** and screen-right is world **−X**. The
   * negative X is forced, not a preference — with a camera above the floor, `right × up` has to
   * point at the viewer, so exactly one of the two axes must be negative.
   */
  read(): RaidInput {
    let moveX = -this.vector.x
    let moveZ = -this.vector.y
    if (this.keys.left) moveX += 1
    if (this.keys.right) moveX -= 1
    if (this.keys.up) moveZ += 1
    if (this.keys.down) moveZ -= 1
    const len = Math.hypot(moveX, moveZ)
    if (len > 1) {
      moveX /= len
      moveZ /= len
    }
    const jump = this.jumpQueued
    this.jumpQueued = false
    return { moveX, moveZ, jump }
  }

  get jumping(): boolean {
    return this.jumpHeld || this.keys.jump
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('pointerdown', this.onPointerDown)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerUp)
    this.stickEl.remove()
  }
}
